import React, { useEffect, useRef, useState } from 'react';

import { CheckIcon, ChevronDownStrokeIcon, CloseIcon } from './Icons';

import './filterComponents.css';

/**
 * A reusable custom select component with dropdown
 *
 * @param {Object} props - Component props
 * @param {Array} props.options - Array of options in the format [{value: 'value', label: 'Label'}]
 * @param {string|null} props.value - Current selected value
 * @param {Function} props.onChange - Function to call when selection changes
 * @param {string} [props.placeholder='Select...'] - Placeholder text when no value is selected
 * @param {boolean} [props.multiple=false] - Whether multiple values can be selected
 * @param {boolean} [props.loading=false] - Whether the options are loading
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {string} [props.label] - Optional label for the select
 * @param {boolean} [props.searchable=false] - Whether to allow searching options
 */
function SelectTW({
    className = '',
    label,
    loading = false,
    multiple = false,
    onChange,
    options = [],
    placeholder = 'Select...',
    searchable = false,
    value = null,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValues, setSelectedValues] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownMenuRef = useRef(null);

    // Set up click outside handler to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchable && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen, searchable]);

    // Update selected values when value prop changes
    useEffect(() => {
        if (value !== null) {
            if (multiple) {
                const values = Array.isArray(value) ? value : [value];
                setSelectedValues(values);
            } else {
                setSelectedValues([value]);
            }
        } else {
            setSelectedValues([]);
        }
    }, [value, multiple]);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setSearchTerm('');
        }
    };

    const handleSelectValue = (optionValue) => {
        let newSelectedValues;

        if (optionValue === 'select-all') {
            newSelectedValues = selectedValues.length === options.length ? [] : options.map((option) => option.value);
        } else if (multiple) {
            newSelectedValues = selectedValues.includes(optionValue)
                ? selectedValues.filter((val) => val !== optionValue)
                : [...selectedValues, optionValue];
        } else {
            newSelectedValues = [optionValue];
            setIsOpen(false);
            setSearchTerm('');
        }

        setSelectedValues(newSelectedValues);

        // Call onChange with the new value(s)
        if (onChange) {
            if (multiple) {
                onChange(newSelectedValues.length === 0 ? null : newSelectedValues);
            } else {
                onChange(newSelectedValues.length === 0 ? null : newSelectedValues[0]);
            }
        }
    };

    const handleDeleteChip = (valueToDelete) => {
        const newValues = selectedValues.filter((val) => val !== valueToDelete);
        setSelectedValues(newValues);

        if (onChange) {
            if (newValues.length === 0) {
                onChange(null);
            } else if (multiple) {
                onChange(newValues);
            } else {
                onChange(newValues[0]);
            }
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const displayValue = () => {
        if (selectedValues.length === 0) {
            return placeholder;
        }
        if (!multiple) {
            // Find matching option to display the label
            const selectedOption = options.find((option) => option.value === selectedValues[0]);
            return selectedOption ? selectedOption.label : selectedValues[0];
        }
        return `${selectedValues.length} selected`;
    };

    const filteredOptions = searchTerm
        ? options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
        : options;

    return (
        <div className={`relative filter-components overflow-visible ${className}`} ref={dropdownRef}>
            {/* Label if provided */}
            {label && <label className="block text-sm font-medium mb-1">{label}</label>}

            {/* Select button */}
            <div
                onClick={toggleDropdown}
                className="flex justify-between items-center w-full px-3 py-2 rounded-md standard-shadow-sm cursor-pointer bg-[var(--primary)] text-[var(--primary-text)] border border-[var(--secondary)] hover:bg-[var(--primary-hover)] hover:text-[var(--primary-hover-text)] focus:outline-none"
            >
                <div className="text-sm truncate">{displayValue()}</div>
                <div className="ml-2">
                    <ChevronDownStrokeIcon
                        className={`w-5 h-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
                    />
                </div>
            </div>

            {/* Dropdown menu */}
            {isOpen && (
                <div
                    ref={dropdownMenuRef}
                    className="absolute z-[100] w-full bg-[var(--shared-white)] border border-[var(--secondary)] rounded-md shadow-lg"
                    style={{
                        maxHeight: '260px',
                        overflowY: 'auto',
                        marginTop: '4px',
                        position: 'absolute',
                        width: '100%',
                    }}
                >
                    {loading ? (
                        <div className="p-3 text-center text-[var(--shared-grey)]">Loading...</div>
                    ) : (
                        <div>
                            {searchable && (
                                <div className="sticky top-0 p-2 bg-[var(--shared-white)] border-b border-[var(--secondary)]">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        className="w-full p-2 text-sm border border-[var(--secondary)] rounded-md bg-[var(--primary)] text-[var(--primary-text)] focus:outline-none"
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                            )}

                            {multiple && options.length > 1 && (
                                <div
                                    className="px-3 py-2 flex justify-between items-center cursor-pointer border-b border-[var(--secondary)] hover:bg-[var(--secondary-hover)] hover:text-[var(--secondary-hover-text)]"
                                    onClick={() => handleSelectValue('select-all')}
                                >
                                    <span>
                                        {selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
                                    </span>
                                    {selectedValues.length === options.length && <CheckIcon className="w-4 h-4" />}
                                </div>
                            )}

                            {filteredOptions.length === 0 ? (
                                <div className="px-3 py-2 text-center text-[var(--shared-grey)]">
                                    No matching options
                                </div>
                            ) : (
                                filteredOptions.map((option) => (
                                    <div
                                        key={option.value}
                                        className="px-3 py-2 flex justify-between items-center cursor-pointer hover:bg-[var(--secondary-hover)] hover:text-[var(--secondary-hover-text)]"
                                        onClick={() => handleSelectValue(option.value)}
                                    >
                                        <span>{option.label}</span>
                                        {selectedValues.includes(option.value) && <CheckIcon className="w-4 h-4" />}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Selected chips for multiple select */}
            {multiple && selectedValues.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                    {selectedValues.map((val) => {
                        const option = options.find((opt) => opt.value === val);
                        const label = option ? option.label : val;

                        return (
                            <div key={val} className="filter-chip header-match">
                                <span>{label}</span>
                                <button onClick={() => handleDeleteChip(val)} title="Remove" type="button">
                                    <CloseIcon />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export { SelectTW };
